import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltIconExternalLinkOutline } from '@cae/cobalt-react/icons/outline/CobaltIconExternalLinkOutline';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { APP_ROUTES } from '@routes/routes.constants';
import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import { FEEDBACK_CONFIGS } from '@shared/components/feedback-content/FeedbackContent.utils';
import { InternalServerErrorIllustration } from '@shared/components/feedback-content/illustrations/InternalServerErrorIllustration';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { EtcaRedirectContentProps } from './EtcaRedirectContent.types';

import { COMMON_TICKET_URL } from './EtcaRedirectContent.utils';

const EtcaRedirectContent = React.memo(
  ({
    state,
    retry,
  }: Readonly<EtcaRedirectContentProps>): React.ReactElement | null => {
    const { isInternalUser } = useAuthenticatedUser();
    const navigate = useNavigate();

    const supportLink = React.useMemo(
      () =>
        isInternalUser ? (
          <CobaltLink href={COMMON_TICKET_URL} target="_blank" inline>
            open an IT ticket
            <CobaltIconExternalLinkOutline />
          </CobaltLink>
        ) : (
          <CobaltLink
            href={import.meta.env.VITE_HELP_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            inline
          >
            contact customer support
            <CobaltIconExternalLinkOutline />
          </CobaltLink>
        ),
      [isInternalUser]
    );

    const handleGoHome = React.useCallback((): void => {
      navigate(APP_ROUTES.APP_INITIALIZER, { replace: true });
    }, [navigate]);

    if (state.step === 'invalid-params') {
      return <InlineFeedback type="bad-request" />;
    }

    if (state.step === 'not-found') {
      return <InlineFeedback type="not-found" />;
    }

    // state.step === 'load-error'
    return (
      <FeedbackContent
        title={FEEDBACK_CONFIGS['internal-server-error'].title}
        message={
          <p>
            Please try again. If the problem persists, please {supportLink}.
          </p>
        }
        illustration={<InternalServerErrorIllustration />}
        primaryActionLabel="Try again"
        onPrimaryAction={retry}
        secondaryActionLabel="Go to Homepage"
        onSecondaryAction={handleGoHome}
      />
    );
  }
);

EtcaRedirectContent.displayName = 'EtcaRedirectContent';

export { EtcaRedirectContent };
